import { Controller } from "@hotwired/stimulus"
import { stream as durableStream } from "@durable-streams/client"

export default class extends Controller {
  static targets = ["speakerList", "extractButton", "extractWrapper", "extractStatus", "emptyState", "emptyStateWithTranscript", "emptyStateNoTranscript", "addForm", "addName", "addRole", "addSummary"]
  static values = {
    activityId: String,
    inloopId: String,
    speakers: { type: Array, default: [] },
    participants: { type: Array, default: [] },
    hasTranscript: { type: Boolean, default: false },
    extractionStreamUrl: String
  }

  connect() {
    this.extractionAbort = null
    this.renderSpeakers()

    // Listen for transcript saved event from sibling tab
    this._onTranscriptSaved = this.onTranscriptSaved.bind(this)
    document.addEventListener("call-transcript:saved", this._onTranscriptSaved)

    // Reconnect to extraction stream on page refresh
    if (this.hasExtractionStreamUrlValue && this.extractionStreamUrlValue) {
      this.setExtractingUI()
      this.connectToExtractionStream(this.extractionStreamUrlValue)
    }
  }

  disconnect() {
    if (this.extractionAbort) {
      this.extractionAbort.abort()
      this.extractionAbort = null
    }
    document.removeEventListener("call-transcript:saved", this._onTranscriptSaved)
  }

  onTranscriptSaved(event) {
    const content = event.detail?.content
    if (content) {
      this.hasTranscriptValue = true
      if (this.hasExtractWrapperTarget) this.extractWrapperTarget.classList.remove("hidden")
      if (this.hasEmptyStateWithTranscriptTarget) this.emptyStateWithTranscriptTarget.classList.remove("hidden")
      if (this.hasEmptyStateNoTranscriptTarget) this.emptyStateNoTranscriptTarget.classList.add("hidden")
    }
  }

  speakersValueChanged() {
    this.renderSpeakers()
  }

  renderSpeakers() {
    if (!this.hasSpeakerListTarget) return

    if (this.speakersValue.length === 0) {
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
      this.speakerListTarget.innerHTML = ""
      return
    }

    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")

    this.speakerListTarget.innerHTML = this.speakersValue.map((speaker, index) => `
      <div class="flex flex-col gap-3 p-4 bg-white border border-gray-200 rounded-xl" data-speaker-index="${index}">
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-center gap-3 flex-1">
            <div class="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
              <span class="text-sm font-semibold text-brand-700">${(speaker.name || "?")[0].toUpperCase()}</span>
            </div>
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <input type="text" value="${this.escapeHtml(speaker.name || "")}"
                     class="text-sm font-semibold text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-500 focus:outline-none px-0 py-0 w-full"
                     placeholder="Speaker name"
                     data-action="change->call-speaker#updateSpeaker"
                     data-field="name"
                     data-index="${index}" />
              <input type="text" value="${this.escapeHtml(speaker.role || "")}"
                     class="text-xs text-gray-500 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-500 focus:outline-none px-0 py-0 w-full"
                     placeholder="Role (e.g., Client, PM, Developer)"
                     data-action="change->call-speaker#updateSpeaker"
                     data-field="role"
                     data-index="${index}" />
              ${this.renderParticipantLink(speaker, index)}
            </div>
          </div>
          <button class="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                  data-action="click->call-speaker#removeSpeaker"
                  data-index="${index}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        ${speaker.summary ? `
          <div class="pl-13 ml-0.5">
            <p class="text-sm text-gray-600 leading-relaxed">${this.escapeHtml(speaker.summary)}</p>
          </div>
        ` : ""}
      </div>
    `).join("")
  }

  // Submit new speaker from modal form
  submitAddSpeaker() {
    const name = this.addNameTarget.value.trim()
    if (!name) return

    const role = this.addRoleTarget.value.trim()
    const summary = this.addSummaryTarget.value.trim()

    const newSpeaker = {
      id: `sp_${Date.now()}`,
      name,
      role,
      summary: summary || null
    }
    this.speakersValue = [...this.speakersValue, newSpeaker]
    this.saveSpeakers()

    // Reset form & close modal
    this.addNameTarget.value = ""
    this.addRoleTarget.value = ""
    this.addSummaryTarget.value = ""

    const modal = this.addFormTarget.closest("[data-controller~='modal']")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      modalController?.close()
    }
  }

  // Update speaker field
  updateSpeaker(e) {
    const index = parseInt(e.target.dataset.index)
    const field = e.target.dataset.field
    const speakers = [...this.speakersValue]
    speakers[index] = { ...speakers[index], [field]: e.target.value }
    this.speakersValue = speakers
    this.saveSpeakers()
  }

  // Remove a speaker
  removeSpeaker(e) {
    const index = parseInt(e.currentTarget.dataset.index)
    const speakers = [...this.speakersValue]
    speakers.splice(index, 1)
    this.speakersValue = speakers
    this.saveSpeakers()
  }

  // Extract speakers from transcript using AI (async via job + durable stream)
  async extractSpeakers() {
    this.setExtractingUI()

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/extract_speakers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.stream_url) {
          this.connectToExtractionStream(data.stream_url)
        }
      } else {
        const error = await response.json()
        this.resetExtractButton()
        if (this.hasExtractStatusTarget) {
          this.extractStatusTarget.textContent = error.error || "Extraction failed"
          this.extractStatusTarget.classList.remove("hidden")
        }
      }
    } catch (error) {
      console.error("Failed to extract speakers:", error)
      this.resetExtractButton()
      if (this.hasExtractStatusTarget) {
        this.extractStatusTarget.textContent = "Extraction failed"
        this.extractStatusTarget.classList.remove("hidden")
      }
    }
  }

  // Connect to durable stream for extraction results
  async connectToExtractionStream(streamUrl) {
    if (this.extractionAbort) {
      this.extractionAbort.abort()
    }

    this.extractionAbort = new AbortController()

    try {
      const res = await durableStream({
        url: streamUrl,
        offset: "-1",
        live: "sse",
        json: true,
        signal: this.extractionAbort.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.handleExtractionEvent(item)
        }
      })

      res.closed.then(() => {
        this.extractionAbort = null
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[call-speaker] Extraction stream error:", error)
      this.resetExtractButton()
      this.extractionAbort = null
    }
  }

  handleExtractionEvent(event) {
    switch (event.type) {
      case "completed":
        if (event.speakers) {
          this.speakersValue = event.speakers
        }
        this.resetExtractButton()
        if (this.hasExtractStatusTarget) {
          this.extractStatusTarget.textContent = `Found ${event.speakers?.length || 0} speakers`
          this.extractStatusTarget.classList.remove("hidden")
        }
        break
      case "error":
        this.resetExtractButton()
        if (this.hasExtractStatusTarget) {
          this.extractStatusTarget.textContent = event.error || "Extraction failed"
          this.extractStatusTarget.classList.remove("hidden")
        }
        break
    }
  }

  setExtractingUI() {
    if (this.hasExtractButtonTarget) {
      this.extractButtonTarget.disabled = true
      this.extractButtonTarget.innerHTML = `
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Extracting...
      `
    }

    if (this.hasExtractStatusTarget) {
      this.extractStatusTarget.textContent = "Analyzing transcript..."
      this.extractStatusTarget.classList.remove("hidden")
    }
  }

  resetExtractButton() {
    if (this.hasExtractButtonTarget) {
      this.extractButtonTarget.disabled = false
      this.extractButtonTarget.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 2.5V17.5M2.5 10H17.5" stroke="currentColor" stroke-width="1.67" stroke-linecap="round"/>
        </svg>
        Extract Speakers
      `
    }
  }

  // Render participant link/badge for a speaker
  renderParticipantLink(speaker, index) {
    if (this.participantsValue.length === 0) return ""

    const linked = speaker.participant_id
      ? this.participantsValue.find(p => String(p.id) === String(speaker.participant_id))
      : null

    if (linked) {
      return `
        <div class="flex items-center gap-1.5 mt-1">
          <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full text-xs font-medium">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${this.escapeHtml(linked.name || linked.email)}
            <button class="ml-0.5 hover:text-brand-900" data-action="click->call-speaker#unlinkParticipant" data-index="${index}">&times;</button>
          </span>
        </div>`
    }

    return `
      <div class="flex flex-col w-full relative mt-1" data-controller="select dropdown" data-action="dropdown:select->select#handleSelect">
        <input type="hidden" data-select-target="hiddenInput" data-action="change->call-speaker#linkParticipant" data-index="${index}" value="" />
        <div class="relative flex items-center w-full rounded-lg overflow-hidden shadow-[0px_1px_2px_rgba(16,24,40,0.05)] h-9 gap-2 transition-all duration-200 border border-[#E4E5E8] bg-white cursor-pointer hover:border-brand-500"
             data-action="click->dropdown#toggle"
             data-dropdown-target="trigger">
          <div class="flex-1 pl-3 pr-10 text-xs truncate" data-dropdown-target="triggerText">
            <span class="text-gray-400">Link to participant...</span>
          </div>
          <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 w-5 h-5">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </div>
        <div class="absolute z-50 flex flex-col items-start p-0 bg-white border border-[#F2F3F5] rounded-lg shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] top-full left-0 mt-1 w-full"
             data-dropdown-target="menu" data-select-target="menu" hidden>
          <div class="flex flex-col items-start w-full max-h-60 overflow-y-auto" data-select-target="optionsContainer">
            <div class="py-1 w-full">
              ${this.participantsValue.map(p => `
                <button type="button"
                        class="w-full text-left px-3 py-2 text-gray-900 hover:bg-gray-50 transition-colors text-xs"
                        data-dropdown-target="item"
                        data-select-target="option"
                        data-value="${p.id}"
                        data-text="${this.escapeHtml(p.name || 'Unnamed')}"
                        data-search-text="${this.escapeHtml((p.name || '').toLowerCase() + ' ' + (p.email || '').toLowerCase())}"
                        data-action="click->dropdown#select">
                  ${this.escapeHtml(p.name || "Unnamed")} <span class="text-gray-400">(${this.escapeHtml(p.email)})</span>
                </button>
              `).join("")}
            </div>
          </div>
        </div>
      </div>`
  }

  // Link a speaker to a participant
  linkParticipant(e) {
    const index = parseInt(e.target.dataset.index)
    const participantId = e.target.value
    const speakers = [...this.speakersValue]
    speakers[index] = { ...speakers[index], participant_id: participantId || null }
    this.speakersValue = speakers
    this.saveSpeakers()
  }

  // Unlink a speaker from a participant
  unlinkParticipant(e) {
    const index = parseInt(e.currentTarget.dataset.index)
    const speakers = [...this.speakersValue]
    speakers[index] = { ...speakers[index], participant_id: null }
    this.speakersValue = speakers
    this.saveSpeakers()
  }

  // Save speakers to server
  async saveSpeakers() {
    try {
      await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/save_configuration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            speakers: this.speakersValue
          })
        }
      )
    } catch (error) {
      console.error("Failed to save speakers:", error)
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
